import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";
import {
  documentBookingSchema,
  DocumentBookingSchema,
} from "../lib/schemas/documentBookingSchema";
import { ServiceData } from "../types/service";
import { useStore } from "../app/stores/store";
import toast from "react-hot-toast";
import { observer } from "mobx-react-lite";

interface ApplyOnlineFormProps {
  services: ServiceData[];
  selectedService: ServiceData;
}

export function ApplyOnlineForm({
  services,
  selectedService,
}: ApplyOnlineFormProps) {
  const navigate = useNavigate();

  const [selectedNationality, setSelectedNationality] = useState<any | null>(
    null
  );
  const [totalPrice, setTotalPrice] = useState(0);
  const [securityDeposit, setSecurityDeposit] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [includeSecurityDeposit, setIncludeSecurityDeposit] = useState(true);
  const [isSecurityDepositAdjustable, setIsSecurityDepositAdjustable] =
    useState(false);
  const [customSecurityDeposit, setCustomSecurityDeposit] = useState<
    number | null
  >(null);
  const [currentService, setCurrentService] =
    useState<ServiceData>(selectedService);

  const {
    bookingStore: { addBooking },
    nationalityStore: {
      loadNationalities,
      NationalitiesDropdown,
      getNatinality,
    },
    userStore,
  } = useStore();

  const isPartner = userStore.isPartner();
  const isAirportCashier = userStore.isAirportCashier();
  const isAdmin = userStore.isAdmin();
  const isCashier = userStore.isCashier();

  const form = useForm<DocumentBookingSchema>({
    resolver: zodResolver(documentBookingSchema),
    defaultValues: {
      customerName: "",
      phone: "",
      email: "",
      entryType: "single",
      duration: "30 days",
      processTime: "Regular - 3-5 working days",
      adultsNumber: 1,
      childrenNumber: 0,
      serviceId: currentService?.id ? currentService.id.toString() : "",
    },
  });

  const { watch, setValue, handleSubmit, control } = form;

  // Load nationalities
  useEffect(() => {
    loadNationalities();
  }, [loadNationalities]);

  // Update serviceId when current service changes
  useEffect(() => {
    if (currentService?.id) {
      setValue("serviceId", currentService.id.toString());
    }
  }, [currentService, setValue]);

  // Update current service when selectedService prop changes
  useEffect(() => {
    if (selectedService) {
      setCurrentService(selectedService);
    }
  }, [selectedService]);

  // Calculate total price whenever relevant fields change
  useEffect(() => {
    const calculateTotalPrice = () => {
      if (!selectedNationality || !currentService) return;

      const adults = Number(watch("adultsNumber")) || 0;
      const children = Number(watch("childrenNumber")) || 0;
      const entryType = watch("entryType");
      const duration = watch("duration");
      const processTime = watch("processTime");

      let nationalityPrice = 0;
      let totalSecurityDeposit = 0;

      if (
        currentService?.name?.includes("Tourist Visa") &&
        includeSecurityDeposit
      ) {
        totalSecurityDeposit = isSecurityDepositAdjustable
          ? (customSecurityDeposit ?? 0) * (adults + children)
          : Number(selectedNationality.securityDeposit || 0) *
            (adults + children);
      }

      if (currentService?.hasNationalityPrice) {
        const singleMonth = Number(
          selectedNationality.singlePriceWithMonth || 0
        );
        const singleMonthChild = Number(
          selectedNationality.singlePriceWithMonthForChild || 0
        );
        const singleTwoMonth = Number(
          selectedNationality.singlePriceWithTwoMonth || 0
        );
        const singleTwoMonthChild = Number(
          selectedNationality.singlePriceWithTwoMonthForChild || 0
        );
        const multipleMonth = Number(
          selectedNationality.multiplePriceWithMonth || 0
        );
        const multipleMonthChild = Number(
          selectedNationality.multiplePriceWithMonthForChild || 0
        );
        const multipleTwoMonth = Number(
          selectedNationality.multiplePriceWithTwoMonth || 0
        );
        const multipleTwoMonthChild = Number(
          selectedNationality.multiplePriceWithTwoMonthForChild || 0
        );

        if (entryType === "single" && duration === "30 days") {
          nationalityPrice = singleMonth * adults + singleMonthChild * children;
        } else if (entryType === "single" && duration === "60 days") {
          let calculatedPrice =
            singleTwoMonth * adults + singleTwoMonthChild * children;
          if (isPartner) calculatedPrice -= 45 * adults;
          nationalityPrice = calculatedPrice;
        } else if (entryType === "multiple" && duration === "30 days") {
          nationalityPrice =
            multipleMonth * adults + multipleMonthChild * children;
        } else if (entryType === "multiple" && duration === "60 days") {
          nationalityPrice =
            multipleTwoMonth * adults + multipleTwoMonthChild * children;
        }

        if (
          isAirportCashier &&
          entryType === "single" &&
          duration === "60 days"
        ) {
          nationalityPrice += 50 * children;
        }
      }

      const processTimePrice = isAirportCashier
        ? 0
        : processTime === "Express - 1~2 working days"
          ? Number(currentService?.expressPrice || 0)
          : Number(currentService?.regularPrice || 0);

      let adjustedAdultPrice = 0;
      let adjustedChildPrice = 0;

      if (isAirportCashier) {
        adjustedAdultPrice =
          Number(currentService?.price || 0) +
          Number(currentService?.airportUserPrice || 0);
        adjustedChildPrice =
          Number(currentService?.childPrice || 0) +
          Number(currentService?.airportUserChildPrice || 0);
      } else if (isPartner) {
        adjustedAdultPrice =
          Number(currentService?.partnerPrice || 0) +
          Number(currentService?.partnerCenterFee || 0);
        adjustedChildPrice =
          Number(currentService?.partnerChildPrice || 0) +
          Number(currentService?.partnerChildCenterFee || 0);
      } else {
        adjustedAdultPrice =
          Number(currentService?.price || 0) +
          Number(currentService?.centerFee || 0);
        adjustedChildPrice =
          Number(currentService?.childPrice || 0) +
          Number(currentService?.childCenterFee || 0);
      }

      if (!currentService?.hasNationalityPrice) {
        const totalAdultsPrice =
          adults * (adjustedAdultPrice + processTimePrice);
        const totalChildrenPrice =
          children * (adjustedChildPrice + processTimePrice);
        const total =
          totalAdultsPrice + totalChildrenPrice + totalSecurityDeposit;
        setTotalPrice(total);
        setValue("totalPrice", total);
        return;
      }

      const totalAdultsPrice = adults * (adjustedAdultPrice + processTimePrice);
      const totalChildrenPrice =
        children * (adjustedChildPrice + processTimePrice);
      const total =
        nationalityPrice +
        totalAdultsPrice +
        totalChildrenPrice +
        totalSecurityDeposit;

      setTotalPrice(total);
      setValue("totalPrice", total);
    };

    calculateTotalPrice();
  }, [
    watch("adultsNumber"),
    watch("childrenNumber"),
    watch("entryType"),
    watch("duration"),
    watch("processTime"),
    currentService,
    selectedNationality,
    includeSecurityDeposit,
    isSecurityDepositAdjustable,
    customSecurityDeposit,
    isPartner,
    isAirportCashier,
    setValue,
  ]);

  const handleNationalityChange = async (selected: string) => {
    const nationality = await getNatinality(selected);
    if (nationality) {
      setSecurityDeposit(
        currentService?.name.includes("Tourist Visa")
          ? Number(nationality.securityDeposit) || 0
          : 0
      );
      setSelectedNationality(nationality);
      setValue("nationalityId", nationality.id);
    }
    setValue("entryType", "single");
    setValue(
      "duration",
      currentService?.name === "Tourist Visa - 2 Month" ? "60 days" : "30 days"
    );
    setValue("processTime", "Regular - 3-5 working days");
  };

  const handleServiceChange = (serviceId: string) => {
    const selected = services.find((s) => s.id.toString() === serviceId);
    if (selected) {
      setCurrentService(selected);
      setValue("entryType", "single");
      setValue(
        "duration",
        selected.name === "Tourist Visa - 2 Month" ? "60 days" : "30 days"
      );
    }
  };

  const onSubmit = async (data: DocumentBookingSchema) => {
    setIsLoading(true);

    if (!data.serviceId) {
      toast.error("Please select a service");
      setIsLoading(false);
      return;
    }

data.includeSecurityDeposit = includeSecurityDeposit;

data.securityDeposit = includeSecurityDeposit
  ? isSecurityDepositAdjustable
    ? (customSecurityDeposit ?? securityDeposit)
    : securityDeposit
  : 0;

data.totalPrice = totalPrice;


    const result = await addBooking(data);
    setIsLoading(false);

    if (result.status === "success") {
      toast.success("Booking added successfully");
      navigate(`/listings/booking/upload/${result.data}`);
    } else {
      toast.error(`Error: ${result.error}`);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-0">
        {/* Customer Name */}
        <FormField
          control={form.control}
          name="customerName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Customer Name</FormLabel>
              <FormControl>
                <Input
                  id="customerNameInput"
                  placeholder="Enter your name"
                  {...field}
                  className="focus-visible:ring-[#e71875] focus-visible:border-[#e71875] transition-colors duration-200"
                />
              </FormControl>
              <FormMessage className="text-red-500" />
            </FormItem>
          )}
        />

        {/* Email */}
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  {...field}
                  className="focus-visible:ring-[#e71875] focus-visible:border-[#e71875] transition-colors duration-200"
                />
              </FormControl>
              <FormMessage className="text-red-500" />
            </FormItem>
          )}
        />

        {/* Phone */}
        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone</FormLabel>
              <FormControl>
                <div className="rounded-md border border-input focus-visible:ring-[#e71875] focus-visible:border-[#e71875] transition-colors duration-200">
                  <PhoneInput
                    international
                    defaultCountry="AE"
                    placeholder="Enter phone number"
                    value={field.value}
                    onChange={field.onChange}
                    className="[&>input]:border-0 [&>input]:ring-0 [&>input]:outline-none [&>input]:focus:ring-0 [&>input]:focus:outline-none flex items-center w-full bg-background px-3 py-2 text-base"
                  />
                </div>
              </FormControl>
              <FormMessage className="text-red-500" />
            </FormItem>
          )}
        />

        {/* Service/Visa Type */}
        <FormField
          control={form.control}
          name="serviceId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Visa Type</FormLabel>
              <Select
                onValueChange={(value) => {
                  field.onChange(value);
                  handleServiceChange(value);
                }}
                value={field.value}
                defaultValue={
                  currentService?.id ? currentService.id.toString() : ""
                }
              >
                <FormControl>
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Select visa type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-white z-50">
                  {services
                    ?.filter((service) => service?.id)
                    .map((service) => (
                      <SelectItem
                        key={service.id}
                        value={service.id.toString()}
                        className="hover:bg-gray-100 "
                      >
                        {service.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <FormMessage className="text-red-500" />
            </FormItem>
          )}
        />

        {/* Nationality */}
        <FormField
          control={form.control}
          name="nationalityId"
          render={() => (
            <FormItem>
              <FormLabel>Nationality</FormLabel>
              <Select
                value={selectedNationality?.id || ""}
                onValueChange={handleNationalityChange}
              >
                <FormControl>
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Select nationality" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-white z-50">
                  {NationalitiesDropdown?.map((nat) => (
                    <SelectItem
                      key={nat.id}
                      value={nat.id}
                      className="hover:bg-gray-100"
                    >
                      {nat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage className="text-red-500" />
            </FormItem>
          )}
        />

        {/* Adults & Children */}
        <div className="grid grid-cols-2 gap-2 ">
          <FormField
            control={form.control}
            name="adultsNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Adults</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={1}
                    max={9}
                    {...field}
                    onChange={(e) =>
                      field.onChange(parseInt(e.target.value) || 1)
                    }
                    className="focus-visible:ring-[#e71875] focus-visible:border-[#e71875] transition-colors duration-200"
                  />
                </FormControl>
                <FormMessage className="text-red-500" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="childrenNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Children</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    max={9}
                    {...field}
                    onChange={(e) =>
                      field.onChange(parseInt(e.target.value) || 0)
                    }
                    className="focus-visible:ring-[#e71875] focus-visible:border-[#e71875] transition-colors duration-200"
                  />
                </FormControl>
                <FormMessage className="text-red-500" />
              </FormItem>
            )}
          />

          {/* Entry Type */}
          <FormItem>
            <FormLabel>Entry Type</FormLabel>
            <div className="flex flex-wrap gap-1">
              <Button
                type="button"
                size="sm"
                variant={
                  watch("entryType") === "single" ? "default" : "outline"
                }
                onClick={() => setValue("entryType", "single")}
              >
                Single
              </Button>
              <Button
                type="button"
                size="sm"
                variant={
                  watch("entryType") === "multiple" ? "default" : "outline"
                }
                onClick={() => setValue("entryType", "multiple")}
              >
                Multiple
              </Button>
            </div>
          </FormItem>

          {/* Duration */}
          <FormItem>
            <FormLabel>Duration</FormLabel>
            <div className="flex flex-wrap gap-1">
              <Button
                type="button"
                size="sm"
                variant={
                  watch("duration") === "30 days" ? "default" : "outline"
                }
                onClick={() => setValue("duration", "30 days")}
              >
                30 Days
              </Button>
              <Button
                type="button"
                size="sm"
                variant={
                  watch("duration") === "60 days" ? "default" : "outline"
                }
                onClick={() => setValue("duration", "60 days")}
              >
                60 Days
              </Button>
            </div>
          </FormItem>
        </div>

        {/* Security Deposit */}
        {selectedNationality?.securityDeposit &&
          currentService?.name?.includes("Tourist Visa") &&
          (isPartner || isAdmin || isCashier) && (
            <div className="space-y-2 mt-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={includeSecurityDeposit}
                  onChange={() =>
                    setIncludeSecurityDeposit(!includeSecurityDeposit)
                  }
                  className="h-4 w-4"
                />
                <span>
                  Include Security Deposit (
                  {selectedNationality?.securityDeposit} AED)
                </span>
              </div>

              {includeSecurityDeposit && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isSecurityDepositAdjustable}
                    onChange={() =>
                      setIsSecurityDepositAdjustable(
                        !isSecurityDepositAdjustable
                      )
                    }
                    className="h-4 w-4"
                  />
                  <span>Adjust Security Deposit?</span>
                </div>
              )}

              {includeSecurityDeposit && isSecurityDepositAdjustable && (
                <Input
                  type="number"
                  min={0}
                  value={customSecurityDeposit ?? ""}
                  onChange={(e) =>
                    setCustomSecurityDeposit(Number(e.target.value))
                  }
                  placeholder="Enter custom security deposit"
                  className="max-w-xs"
                />
              )}
            </div>
          )}

        {/* {selectedNationality?.securityDeposit &&
          currentService?.name?.includes("Tourist Visa") &&
          isPartner || isAdmin && (
            <div className="space-y-2 mt-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={includeSecurityDeposit}
                  onChange={() =>
                    setIncludeSecurityDeposit(!includeSecurityDeposit)
                  }
                  className="h-4 w-4"
                />
                <span>
                  Include Security Deposit (
                  {selectedNationality.securityDeposit} AED)
                </span>
              </div>
              {includeSecurityDeposit && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isSecurityDepositAdjustable}
                    onChange={() =>
                      setIsSecurityDepositAdjustable(
                        !isSecurityDepositAdjustable
                      )
                    }
                    className="h-4 w-4"
                  />
                  <span>Adjust Security Deposit?</span>
                </div>
              )}
              {includeSecurityDeposit && isSecurityDepositAdjustable && (
                <Input
                  type="number"
                  min={0}
                  value={customSecurityDeposit ?? ""}
                  onChange={(e) =>
                    setCustomSecurityDeposit(Number(e.target.value))
                  }
                  placeholder="Enter custom security deposit"
                  className="max-w-xs"
                />
              )}
            </div>
          )} */}

        {/* Total Price */}
        <div className="mt-4 mb-2 font-bold text-end text-lg">
          Total Price: <span className="text-primary">{totalPrice} AED</span>
        </div>

        {/* Submit Button - Always Unclickable */}
        {/* <Button
          type="submit"
          disabled={!form.watch("serviceId")} // <-- button enabled only if visa type selected
          className={`w-full text-lg py-6 ${
            form.watch("serviceId")
              ? "bg-primary hover:bg-primary/90 cursor-pointer"
              : "bg-primary opacity-50 cursor-not-allowed"
          }`}
        >
          Book Now
        </Button> */}

        <Button
          type="submit"
          disabled={
            !form.watch("serviceId") ||
            currentService?.name === "Resort - Ras Al Khaimah"
          }
          className={`w-full text-lg py-6 ${
            form.watch("serviceId") &&
            currentService?.name !== "Resort - Ras Al Khaimah"
              ? "bg-primary hover:bg-primary/90 cursor-pointer"
              : "bg-primary opacity-50 cursor-not-allowed"
          }`}
        >
          {isLoading ? "Processing..." : "Book Now"}
        </Button>
      </form>
    </Form>
  );
}

export default observer(ApplyOnlineForm);
